import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { API, axios } from '../utils/api';
import { allineaNotifiche, unsubscribeFromPush } from '../utils/serviceWorker';
import { leggiSessione, salvaSessione, cancellaSessione, aggiornaInfoSessione, segnaSessioneScaduta } from '../utils/sessione';
import { linguaDelUtente } from '../i18n';

const AuthContext = createContext();

// La pagina di arrivo di ogni ruolo (dopo l'accesso, o aprendo /login quando si è già entrati)
const paginaDelRuolo = (userType) => (
  userType === 'admin' ? '/admin' : userType === 'collaboratore' ? '/collaboratore' : '/dashboard'
);

// Dopo l'accesso si torna alla pagina da cui si era partiti (un link da un'email o da WhatsApp aperto da scollegati:
// /firma/fi-p1, /profilo#privacy, /immobili/i-r1; o la pagina su cui la sessione era scaduta), se è una pagina del
// proprio ruolo. Solo percorsi interni: "/" iniziale, mai "//" o una barra rovesciata (porterebbero a un altro sito),
// niente pagine di accesso; altrimenti la pagina del ruolo. Il percorso arriva al login nell'indirizzo (?torna=).
const PAGINE_DEL_RUOLO = {
  cliente: /^\/(dashboard|immobili|calendario|documenti|profilo|firma\/)/,
  admin: /^\/(admin|calendario|profilo|documenti|immobili\/|agenzie|clienti\/|matching)/,
  collaboratore: /^\/(collaboratore|admin\/(agenda|clienti|documenti|archivio)|calendario|profilo|documenti|immobili\/)/,
};
const percorsoInterno = (torna) => {
  if (typeof torna !== 'string' || torna.length > 500) return null;
  const t = torna.trim();
  if (!t.startsWith('/') || t.startsWith('//')) return null;
  // niente barre rovesciate (il browser le legge come "/": "/\altro.it") né caratteri di controllo (a capo, tab)
  if ([...t].some((c) => c === '\\' || c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127)) return null;
  return t;
};
const destinazioneDopoAccesso = (torna, userType) => {
  const t = percorsoInterno(torna);
  return t && PAGINE_DEL_RUOLO[userType]?.test(t) ? t : paginaDelRuolo(userType);
};
// Il login con la pagina di partenza: "/login?torna=%2Ffirma%2Ffi-p1"
const loginPer = (percorso) => {
  const t = percorsoInterno(percorso);
  return t && t !== '/' && !/^\/(login|register)/.test(t) ? `/login?torna=${encodeURIComponent(t)}` : '/login';
};

// Pagine che si vedono senza login: se si esce da un'altra scheda restano dove sono
const PAGINE_PUBBLICHE = /^\/(login|register|register-collaboratore|privacy|termini|condivisi\/)/;
const PAGINE_DI_ACCESSO = /^\/(login|register|register-collaboratore)\/?$/;

// Uscita fatta in un'altra scheda: il login lo dice (una volta sola)
const USCITA_ALTRA_SCHEDA = 'mk_uscita_altra_scheda';
const uscitaDaAltraScheda = () => {
  try {
    const si = sessionStorage.getItem(USCITA_ALTRA_SCHEDA) === '1';
    sessionStorage.removeItem(USCITA_ALTRA_SCHEDA);
    return si;
  } catch { return false; }
};

// Chi è entrato: lo stesso utente anche se il token cambia (nuovo login, token rinnovato)
const identita = (sessione) => (sessione ? `${sessione.userType}:${sessione.userInfo?.id || sessione.userInfo?.email || ''}` : '');

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  // "Esci" toccato in questa scheda: ProtectedRoute porta al login semplice, senza ?torna= della pagina lasciata
  // (prima il cliente che entrava dopo finiva sulla pagina del precedente, per esempio /documenti o /firma/…)
  const [uscitaVolontaria, setUscitaVolontaria] = useState(false);
  // Token di questa scheda: per capire, quando un'altra scheda cambia il login, se è ancora il nostro
  const tokenAttuale = useRef(null);

  // Toglie la sessione da questa scheda (senza avvisare il server)
  const pulisci = () => {
    cancellaSessione();
    tokenAttuale.current = null;
    setUser(null);
    setUserType(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  useEffect(() => {
    const sessione = leggiSessione();
    if (sessione) {
      const { token } = sessione;
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 > Date.now()) {
          tokenAttuale.current = token;
          setUserType(sessione.userType);
          setUser(sessione.userInfo);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          // App riaperta con il login già dentro: si finisce un eventuale rinnovo dell'iscrizione alle notifiche
          // lasciato dal service worker. Nessun permesso viene chiesto qui.
          allineaNotifiche().catch(() => {});
        } else {
          segnaSessioneScaduta();
          pulisci();
        }
      } catch (error) {
        pulisci();
      }
    }
    setLoading(false);
  }, []);

  // Due schede aperte: il login sta in localStorage, condiviso. Se un'altra scheda esce, anche questa esce (e
  // ProtectedRoute la porta al login); se nell'altra entra un utente diverso, questa si ricarica sulla sua pagina (mai dati del vecchio
  // utente mescolati al nuovo). salvaSessione prima cancella e poi scrive: si guarda lo stato a eventi finiti.
  useEffect(() => {
    let attesa = null;
    const controlla = () => {
      attesa = null;
      const sessione = leggiSessione();
      if (sessione?.token === tokenAttuale.current) return;
      const percorso = window.location.pathname;
      if (!sessione) {
        if (!tokenAttuale.current) return;
        // Sulle pagine protette ProtectedRoute porta al login appena l'utente sparisce, e il login lo dice
        // (niente ricaricamento: consumerebbe l'avviso prima della pagina nuova)
        if (!PAGINE_PUBBLICHE.test(percorso)) {
          try { sessionStorage.setItem(USCITA_ALTRA_SCHEDA, '1'); } catch { /* archivio non disponibile */ }
        }
        pulisci();
        return;
      }
      const stessoUtente = tokenAttuale.current && identita(sessione) === identita({ userType, userInfo: user });
      if (stessoUtente) {
        // stesso utente, token nuovo (è rientrato nell'altra scheda): si usa quello
        tokenAttuale.current = sessione.token;
        axios.defaults.headers.common['Authorization'] = `Bearer ${sessione.token}`;
        setUser(sessione.userInfo);
        return;
      }
      if (!tokenAttuale.current && PAGINE_PUBBLICHE.test(percorso) && !PAGINE_DI_ACCESSO.test(percorso)) {
        // privacy, termini, link condivisi: nessun dato di un utente sulla pagina, si prende il login e basta
        tokenAttuale.current = sessione.token;
        axios.defaults.headers.common['Authorization'] = `Bearer ${sessione.token}`;
        setUserType(sessione.userType);
        setUser(sessione.userInfo);
        return;
      }
      // un altro utente (o il primo accesso mentre questa scheda era sul login): si riparte dalla sua pagina
      tokenAttuale.current = sessione.token;
      window.location.replace(paginaDelRuolo(sessione.userType));
    };
    const alCambio = (e) => {
      if (e.storageArea && e.storageArea !== localStorage) return;
      if (e.key !== null && !['token', 'userType', 'userInfo'].includes(e.key)) return;
      clearTimeout(attesa);
      attesa = setTimeout(controlla, 50);
    };
    window.addEventListener('storage', alCambio);
    return () => { window.removeEventListener('storage', alCambio); clearTimeout(attesa); };
  }, [user, userType]);

  // Risposta di /auth/login (uguale per codice via email e passkey) → sessione nel browser
  const accedi = (dati, restaCollegato = true) => {
    const { access_token, user_type, user_info } = dati;
    salvaSessione({ token: access_token, userType: user_type, userInfo: user_info }, restaCollegato);
    tokenAttuale.current = access_token;
    setUscitaVolontaria(false);
    setUser(user_info);
    setUserType(user_type);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    // Notifiche: nessun permesso viene chiesto qui (lo chiede la card della home, con un tocco). Si riprende solo
    // quello che era rimasto in sospeso e, se il permesso c'era già, si rinfresca l'iscrizione di questo dispositivo.
    allineaNotifiche().catch(() => {});
    return { success: true, userType: user_type, userInfo: user_info };
  };

  // Errore del login da mostrare: 5xx e rete → invito a riprovare; mai un detail che non sia testo
  const erroreAccesso = (error, ripiego) => {
    const status = error.response?.status;
    if (!status || status >= 500) return 'Il servizio non risponde: riprovate tra poco.';
    const detail = error.response?.data?.detail;
    return typeof detail === 'string' && detail.trim() ? detail : ripiego;
  };

  const login = async (email, password, restaCollegato = true) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      return accedi(response.data, restaCollegato);
    } catch (error) {
      return {
        success: false,
        status: error.response?.status,
        error: erroreAccesso(error, 'Accesso non riuscito: riprovate.'),
      };
    }
  };

  // Uscita: il server rende subito non valido il token di questa sessione (POST /auth/esci). Non si aspetta la
  // risposta e un errore non ferma l'uscita. Prima il token dell'archivio: dopo un cambio password usaNuovoToken
  // (utils/api.js) lo sostituisce lì senza passare di qui.
  const logout = () => {
    const token = leggiSessione()?.token || tokenAttuale.current;
    if (token) {
      // prima si toglie l'iscrizione alle notifiche di questo dispositivo (dopo "Esci" non devono più arrivare le
      // notifiche del cliente, con titolo e testo), poi si chiude la sessione: in ogni caso, anche se la
      // disiscrizione non riesce o ci mette più di 3 secondi (unsubscribeFromPush non resta mai in attesa)
      const esci = () => axios.post(`${API}/auth/esci`, null, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      unsubscribeFromPush(token).then(esci, esci);
    }
    setUscitaVolontaria(true);
    pulisci();
  };

  const register = async (formData) => {
    try {
      const response = await axios.post(`${API}/clienti/registrazione`, formData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: erroreAccesso(error, 'Registrazione non riuscita: riprovate.'),
      };
    }
  };

  // Il nome cambiato nel profilo (in alto a destra). La risposta del salvataggio può arrivare dopo «Esci» (rete lenta):
  // allora non c'è più niente da aggiornare. Prima rimetteva l'utente di prima (senza ruolo) e il login rimandava a
  // /dashboard, ferma su «Caricamento…». Nemmeno se nel frattempo nel browser è entrato qualcun altro (un'altra scheda):
  // il nome del vecchio utente non va nella sessione del nuovo.
  const updateUser = (updatedFields) => {
    const sessione = leggiSessione();
    if (!sessione || !tokenAttuale.current) return;
    if (identita(sessione) !== identita({ userType, userInfo: user })) return;
    aggiornaInfoSessione({ ...sessione.userInfo, ...updatedFields });
    setUser((u) => (u ? { ...u, ...updatedFields } : u));
  };

  return (
    <AuthContext.Provider value={{
      user,
      userType,
      // La lingua dell'app: quella scelta dal cliente nel profilo (campo `lingua`), "it" se non c'è o non la
      // conosciamo. Lo staff lavora sempre in italiano. LayoutCliente la passa alle pagine (FornitoreLingua).
      lingua: linguaDelUtente(userType, user),
      loading,
      login,
      accedi,
      logout,
      register,
      updateUser,
      uscitaVolontaria,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const ProtectedRoute = ({ children, adminOnly = false, allowCollaboratore = false }) => {
  const { isAuthenticated, userType, loading, uscitaVolontaria } = useAuth();
  const { pathname, search, hash } = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Caricamento...</div>
      </div>
    );
  }

  // Scollegati (link aperto da fuori, sessione scaduta all'apertura, uscita da un'altra scheda): al login con la
  // pagina di partenza, per tornarci dopo l'accesso (prima si finiva sempre su /dashboard). Dopo "Esci" invece il
  // login semplice: chi entra dopo (un altro cliente sullo stesso telefono) parte dalla sua pagina
  if (!isAuthenticated) {
    return <Navigate to={uscitaVolontaria ? '/login' : loginPer(`${pathname}${search}${hash}`)} replace />;
  }

  if (adminOnly && userType !== 'admin') {
    if (allowCollaboratore && userType === 'collaboratore') {
      return children;
    }
    if (userType === 'collaboratore') {
      // "/admin?persona=…" scritto a mano: il filtro (e l'ancora) restano, invece di perdersi nel rimbalzo
      return <Navigate to={`/collaboratore${search}${hash}`} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export {
  AuthContext, AuthProvider, useAuth, ProtectedRoute, paginaDelRuolo, uscitaDaAltraScheda, destinazioneDopoAccesso, loginPer,
};
